import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { NEW_DECLARATION, NEW_DECLARATION_WITH_EQUIPMENT } from '10.quickConnect.app/routes/paths';
import { useStore } from '30.quickConnect.Stores';
import { FormViewer } from '90.quickConnect.Models/models';
import { useIsMobile } from '80.quickConnect.Core/hooks';
import { FEATURE_013 } from '30.quickConnect.Stores/RootStore/LoginStore/const';
import { DeclarationLocationState } from '10.quickConnect.app/components/domain/Declaration/types';
import useOpenedElsewherePrompt from '10.quickConnect.app/components/shared/Dialogs/OpenedElsewherePrompt/useOpenedElsewherePrompt';

const useData = (form: FormViewer) => {
  const { id, name, entityInstanceId } = form;
  const { t } = useTranslation('forms');

  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [isHover, setIsHover] = useState<boolean>(false);

  const {
    LoginStore: { checkForFeature },
    UserSettingsStore: { formsAsTree },
    FormStore: { isFavoriteForm, toggleFavorite: toggleFavoriteForm },
    DeclarationStore: {
      DraftsStore: { items: declarations = [] },
      isFormEditedElsewhereAsync,
    },
  } = useStore();

  const isFavoritesFeatureEnabled = checkForFeature(FEATURE_013);

  const [choiceDialogOpen, setChoiceDialogOpen] = useState<boolean>(false);
  const closeChoiceDialog = useCallback(() => setChoiceDialogOpen(false), []);

  const { showPrompt, promptProps: openedElsewherePromptProps } = useOpenedElsewherePrompt();

  const drafts = useMemo(() => declarations.filter((declaration) => declaration.formId === id), [declarations, id]);

  const navigateToNewDeclaration = useCallback(
    (state?: DeclarationLocationState) => {
      let link = NEW_DECLARATION.replace(':formId', id);

      if (entityInstanceId) {
        link = NEW_DECLARATION_WITH_EQUIPMENT.replace(':formId', id).replace(':entityInstanceId', entityInstanceId);
      }

      navigate(link, { state });
    },
    [entityInstanceId, id, navigate],
  );

  const createDeclaration = useCallback(() => navigateToNewDeclaration(), [navigateToNewDeclaration]);

  // Formulaire déjà en cours de saisie, dans un autre onglet ou en brouillon : l'utilisateur choisit de commencer une
  // nouvelle saisie, ou de reprendre un brouillon
  const onClickForm = useCallback(async () => {
    if (await isFormEditedElsewhereAsync(id))
      showPrompt({
        kind: 'form',
        name,
        // La nouvelle saisie ne redemande pas confirmation à son ouverture
        action: () => navigateToNewDeclaration({ isNewEntryConfirmed: true }),
      });
    else if (drafts.length > 0) setChoiceDialogOpen(true);
    else createDeclaration();
  }, [id, name, drafts, isFormEditedElsewhereAsync, showPrompt, navigateToNewDeclaration, createDeclaration]);

  const toggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleFavoriteForm(form.id, t, isFavoritesFeatureEnabled);
  };

  return {
    drafts,
    choiceDialogOpen,
    onClickForm,
    createDeclaration,
    closeChoiceDialog,
    openedElsewherePromptProps,
    isMobile,
    isHover,
    setIsHover,
    toggleFavorite,
    isFavorite: isFavoriteForm(form.id),
    formsAsTree,
    isFavoritesFeatureEnabled,
  };
};

export default useData;
